import {
  LoaderCircle,
  Save,
} from 'lucide-react'
import {
  useEffect,
  useRef,
  useState,
} from 'react'

import ModalBase, {
  AvisoModal,
  CampoModal,
  estiloInputModal,
} from '../abastecimiento/ModalBase'
import { registrarPagoCompra } from '../../services/cuentasPorPagar'
import { nombreProveedorCompra } from '../../utils/compras'
import {
  esErrorDefinitivo,
  LIMITE_NUMERO_DOCUMENTO,
  LIMITE_OBSERVACIONES,
  LIMITE_REFERENCIA,
  MEDIOS_PAGO,
  respaldoRequiereNumero,
  TIPOS_RESPALDO,
  validarPagoCompra,
} from '../../utils/cuentasPorPagar'
import {
  formatearMontoPos,
  obtenerFechaActualPos,
} from '../../utils/pos'

function crearFormularioInicial() {
  return {
    fecha: obtenerFechaActualPos(),
    monto: '',
    medioPago: '',
    referencia: '',
    tipoRespaldo: 'SIN_DOCUMENTO',
    numeroDocumento: '',
    observaciones: '',
  }
}

function crearClaveIdempotencia() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()

  return `${Date.now()}-${Math.random()}`
}

function MensajeCampo({ mensaje }) {
  if (!mensaje) return null

  return <p className="mt-1 text-sm text-red-600">{mensaje}</p>
}

// Formulario de abono (registrar_pago_compra). El documento NO es
// obligatorio. La clave de idempotencia identifica la intención de pago y se
// reutiliza en los reintentos; solo se renueva al abrir el formulario de nuevo.
function RegistrarPagoModal({
  abierto,
  fila,
  empresaId,
  onCerrar,
  onRegistrado,
}) {
  const [formulario, setFormulario] = useState(crearFormularioInicial)
  const [mostrarErrores, setMostrarErrores] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [errorEnvio, setErrorEnvio] = useState('')
  // Falla sin respuesta de Supabase (red): el pago pudo haberse registrado.
  const [resultadoIncierto, setResultadoIncierto] = useState(false)
  const enCursoRef = useRef(false)
  const claveRef = useRef(crearClaveIdempotencia())

  useEffect(() => {
    if (!abierto) return

    setFormulario(crearFormularioInicial())
    setMostrarErrores(false)
    setErrorEnvio('')
    setResultadoIncierto(false)
    enCursoRef.current = false
    // Una clave nueva por cada apertura: los reintentos del mismo envío
    // reutilizan la clave y el backend podrá descartar duplicados.
    claveRef.current = crearClaveIdempotencia()
  }, [abierto, fila?.compra?.id])

  if (!abierto || !fila) return null

  const { compra } = fila
  const hoy = obtenerFechaActualPos()

  const validacion = validarPagoCompra(formulario, {
    saldoCentavos: fila.saldoCentavos,
    hoy,
    fechaCompra: compra.fecha_compra,
  })

  const errores = mostrarErrores ? validacion.errores : {}
  const saldoDespuesCentavos =
    validacion.montoCentavos !== null && fila.saldoCentavos !== null
      ? fila.saldoCentavos - validacion.montoCentavos
      : null

  const actualizar = (campo, valor) => {
    setFormulario((actual) => ({ ...actual, [campo]: valor }))
    setErrorEnvio('')
  }

  const confirmar = async (event) => {
    event.preventDefault()

    if (enCursoRef.current) return

    setMostrarErrores(true)

    if (!validacion.valido) return

    enCursoRef.current = true
    setEnviando(true)

    try {
      await registrarPagoCompra({
        empresaId,
        compraId: compra.id,
        fecha: formulario.fecha,
        monto: validacion.montoCentavos / 100,
        medioPago: formulario.medioPago,
        referencia: formulario.referencia,
        tipoRespaldo: formulario.tipoRespaldo,
        numeroDocumento: formulario.numeroDocumento,
        observaciones: formulario.observaciones,
        claveIdempotencia: claveRef.current,
      })

      onRegistrado?.()
    } catch (errorRegistro) {
      if (esErrorDefinitivo(errorRegistro)) {
        // Supabase rechazó el pago: no se registró nada.
        setErrorEnvio(
          errorRegistro.message || 'No fue posible registrar el pago.'
        )
      } else {
        // Sin respuesta: se conserva la misma clave y los mismos datos para
        // que reintentar nunca duplique el pago.
        setResultadoIncierto(true)
        setErrorEnvio(
          'No se pudo confirmar si el pago se registró (problema de conexión). Revisa el historial antes de repetirlo; si reintentas aquí, no se duplicará.'
        )
      }

      enCursoRef.current = false
    } finally {
      setEnviando(false)
    }
  }

  return (
    <ModalBase
      abierto={abierto}
      titulo="Registrar pago"
      tituloId="titulo-registrar-pago"
      procesando={enviando}
      onCerrar={onCerrar}
    >
      <form
        onSubmit={confirmar}
        noValidate
        className="mt-5 space-y-4"
      >
        <fieldset
          disabled={enviando || resultadoIncierto}
          className="space-y-4"
        >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CampoModal label="Compra" htmlFor="pago-compra">
            <input
              id="pago-compra"
              type="text"
              value={compra.codigo_compra || ''}
              readOnly
              className={estiloInputModal}
            />
          </CampoModal>

          <CampoModal label="Proveedor" htmlFor="pago-proveedor">
            <input
              id="pago-proveedor"
              type="text"
              value={nombreProveedorCompra(compra)}
              readOnly
              className={estiloInputModal}
            />
          </CampoModal>
        </div>

        <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
          Total obligación:{' '}
          <strong className="text-slate-900">
            {formatearMontoPos(fila.totalCentavos / 100)}
          </strong>
          {' · '}
          Saldo:{' '}
          <strong className="text-slate-900">
            {formatearMontoPos((fila.saldoCentavos || 0) / 100)}
          </strong>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CampoModal label="Fecha del pago" htmlFor="pago-fecha" requerido>
            <input
              id="pago-fecha"
              type="date"
              max={hoy}
              value={formulario.fecha}
              onChange={(event) => actualizar('fecha', event.target.value)}
              className={estiloInputModal}
            />
            <MensajeCampo mensaje={errores.fecha} />
          </CampoModal>

          <CampoModal label="Monto ($)" htmlFor="pago-monto" requerido>
            <input
              id="pago-monto"
              type="text"
              inputMode="decimal"
              value={formulario.monto}
              onChange={(event) => actualizar('monto', event.target.value)}
              placeholder="500.000"
              className={estiloInputModal}
            />
            <MensajeCampo mensaje={errores.monto} />
            {saldoDespuesCentavos !== null && saldoDespuesCentavos >= 0 && (
              <p className="mt-1 text-xs text-slate-500">
                Saldo después del pago:{' '}
                {formatearMontoPos(saldoDespuesCentavos / 100)}
              </p>
            )}
          </CampoModal>

          <CampoModal
            label="Medio de pago"
            htmlFor="pago-medio"
            requerido
          >
            <select
              id="pago-medio"
              value={formulario.medioPago}
              onChange={(event) =>
                actualizar('medioPago', event.target.value)
              }
              className={estiloInputModal}
            >
              <option value="">Seleccionar</option>
              {MEDIOS_PAGO.map((medio) => (
                <option
                  key={medio.valor}
                  value={medio.valor}
                >
                  {medio.etiqueta}
                </option>
              ))}
            </select>
            <MensajeCampo mensaje={errores.medioPago} />
          </CampoModal>

          <CampoModal
            label="Referencia / N° de operación"
            htmlFor="pago-referencia"
          >
            <input
              id="pago-referencia"
              type="text"
              maxLength={LIMITE_REFERENCIA}
              value={formulario.referencia}
              onChange={(event) =>
                actualizar('referencia', event.target.value)
              }
              className={estiloInputModal}
            />
            <MensajeCampo mensaje={errores.referencia} />
          </CampoModal>

          <CampoModal
            label="Tipo de respaldo"
            htmlFor="pago-respaldo"
            requerido
          >
            <select
              id="pago-respaldo"
              value={formulario.tipoRespaldo}
              onChange={(event) =>
                actualizar('tipoRespaldo', event.target.value)
              }
              className={estiloInputModal}
            >
              {TIPOS_RESPALDO.map((respaldo) => (
                <option
                  key={respaldo.valor}
                  value={respaldo.valor}
                >
                  {respaldo.etiqueta}
                </option>
              ))}
            </select>
            <MensajeCampo mensaje={errores.tipoRespaldo} />
          </CampoModal>

          <CampoModal
            label="N° de documento (opcional)"
            htmlFor="pago-documento"
          >
            <input
              id="pago-documento"
              type="text"
              maxLength={LIMITE_NUMERO_DOCUMENTO}
              value={formulario.numeroDocumento}
              disabled={!respaldoRequiereNumero(formulario.tipoRespaldo)}
              onChange={(event) =>
                actualizar('numeroDocumento', event.target.value)
              }
              className={estiloInputModal}
            />
            <MensajeCampo mensaje={errores.numeroDocumento} />
          </CampoModal>
        </div>

        <CampoModal label="Observaciones" htmlFor="pago-observaciones">
          <textarea
            id="pago-observaciones"
            rows={3}
            maxLength={LIMITE_OBSERVACIONES}
            value={formulario.observaciones}
            onChange={(event) =>
              actualizar('observaciones', event.target.value)
            }
            className={estiloInputModal}
          />
          <MensajeCampo mensaje={errores.observaciones} />
        </CampoModal>

        </fieldset>

        <p className="text-xs text-slate-500">
          El documento no es obligatorio: puedes registrar el pago sin
          factura y asociarla después.
        </p>

        {errorEnvio && <AvisoModal tipo="error">{errorEnvio}</AvisoModal>}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCerrar}
            disabled={enviando}
            className="min-h-12 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={enviando}
            className="flex min-h-12 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {enviando ? (
              <LoaderCircle
                size={18}
                className="animate-spin"
              />
            ) : (
              <Save size={18} />
            )}
            {resultadoIncierto ? 'Reintentar pago' : 'Confirmar pago'}
          </button>
        </div>
      </form>
    </ModalBase>
  )
}

export default RegistrarPagoModal
